import { motion, useReducedMotion } from 'motion/react';
import { PlayerHex, type PlayerHexConfig } from './PlayerHex';

/**
 * Player roster + per-dot motion specs.
 *
 * Paths and durations come from the corresponding control-hub
 * PlayerGraphics/*Graphic.jsx file. Each control-hub graphic drove c1/c2
 * with either a GSAP `attr` tween (a straight line between two coords —
 * mapped here to a 2-point path with mode='yoyo') or a GSAP `motionPath`
 * along an SVG path (mapped to mode='loop' along the same path string).
 *
 * The pieces.svg files originally baked in static dot circles at the
 * starting anchor of each path. Those have been removed from the SVGs
 * so the *only* visible dots on the hex are the animated ones below.
 */
const PARTS = '/assets/images/players/parts';

const PLAYERS: PlayerHexConfig[] = [
  {
    slug: 'cinna',
    name: 'Cinna',
    photo: `${PARTS}/Cinna_PlayerImage.png`,
    pieces: `${PARTS}/Cinna_pieces.svg`,
    dots: [
      // c1: GSAP attr lerp (345.69, 413.19) ↔ (230, 443) — straight line, yoyo
      { path: 'M345.69 413.19 L230 443', duration: 1.8, mode: 'yoyo' },
      // c2: GSAP motionPath along the swirly logo's inner curve — closed loop
      {
        path: 'M314.47 422.11C313.95 422.11 313.47 421.82 313 421.54C311.31 420.46 309.74 419.21 308.41 418.12C302.73 413.43 284.38 396.92 287.07 381.37C288.3 374.26 294.39 369.31 303.79 367.77C311.81 366.45 325.41 366.6 344.61 374.74C357.94 380.39 370.72 388.64 379.67 397.38L380.03 397.73C380.92 398.59 382.01 399.65 382.67 400.57C383.24 401.19 383.22 401.85 383.1 402.3L382.87 403.14L381.43 403.51C378.48 404.27 375.42 405.05 372.47 405.93C372.22 406.01 371.58 406.19 371.58 406.19L370.75 406.43L370.13 405.83C362.67 398.61 352.81 392.97 343.76 390.77C338.63 389.52 329.64 388.91 325.92 392.35C324.4 393.76 323.86 395.75 324.29 398.43C325.04 403.08 328.66 408.13 335.34 413.86L337.45 415.67L314.99 422.02C314.81 422.09 314.63 422.12 314.46 422.12L314.47 422.11Z',
        duration: 5.0,
        mode: 'loop',
      },
    ],
  },
  {
    slug: 'marlon',
    name: 'Marlon',
    photo: `${PARTS}/Marlon_PlayerImage.png`,
    pieces: `${PARTS}/Marlon_pieces.svg`,
    dots: [
      // c1: GSAP motionPath along the triangle leg of the logo — closed loop
      {
        path: 'M380.61 423.96H408.25L368.11 323.45C365.51 329.37 362.74 336.09 360.05 342.63C357.73 348.26 355.54 353.59 353.63 357.95L380.61 423.96Z',
        duration: 3.0,
        mode: 'loop',
      },
      // c2: GSAP attr lerp cx 231.51→321.58 at y=425.83 — horizontal yoyo
      { path: 'M231.51 425.83 L321.58 425.83', duration: 1.6, mode: 'yoyo' },
    ],
  },
  {
    slug: 'plaqueboymax',
    name: 'Plaqueboymax',
    photo: `${PARTS}/Plaqueboymax_PlayerImage.png`,
    pieces: `${PARTS}/Plaqueboymax_pieces.svg`,
    dots: [
      // c1 + c2: both motionPath along closed quads (the two angled bracket bars)
      { path: 'M317.48 354.45V419.55L340.86 406.22V341.36Z', duration: 3.0, mode: 'loop' },
      { path: 'M390.76 385.36V450.46L414.14 437.13V372.26Z', duration: 3.0, mode: 'loop' },
    ],
  },
  {
    slug: 'jasontheween',
    name: 'JASONtheween',
    photo: `${PARTS}/Jason_PlayerImage.png`,
    pieces: `${PARTS}/Jason_pieces.svg`,
    dots: [
      // Two dots ride the same diagonal stripe in opposite phase — yoyo + reverse
      { path: 'M296.64 374.13 L262.23 462.58', duration: 1.8, mode: 'yoyo' },
      { path: 'M296.64 374.13 L262.23 462.58', duration: 1.8, mode: 'yoyo', reverse: true },
    ],
  },
  {
    slug: 'emiru',
    name: 'Emiru',
    photo: `${PARTS}/Emiru_PlayerImage.png`,
    pieces: `${PARTS}/Emiru_pieces.svg`,
    dots: [
      // c1: motionPath along the stepped-box top edge
      {
        path: 'M337.49 377.64L367.17 347.69C370.3 345.01 374.08 343.53 378.76 343.15H378.88C378.88 343.15 388.2 343.13 397.53 343.13H410.36C414.66 343.13 416.96 343.28 417.26 343.58L417.69 344.01V377.37L337.48 377.63L337.49 377.64Z',
        duration: 3.5,
        mode: 'loop',
      },
      // c2: motionPath along the small triangle in the bottom-left
      { path: 'M281.1 435.21V461.46L307.35 435.21H281.1Z', duration: 2.0, mode: 'loop' },
    ],
  },
  {
    slug: 'ishowspeed',
    name: 'IShowSPEED',
    photo: `${PARTS}/ISHOWSPEED_PlayerImage.png`,
    pieces: `${PARTS}/ISHOWSPEED_pieces.svg`,
    dots: [
      // c1: motionPath around the main arrow's inner edge
      {
        path: 'M251.81 397.49L274.53 421.03C276.93 423.51 278.03 425.79 277.88 428C277.75 430.02 276.61 431.86 274.38 433.63L232.41 469.03C239 465.59 245.95 461.98 252.74 458.46C277.45 445.62 303 432.35 306.39 429.95C309.12 427.67 309.05 424.73 306.18 421.19L284.04 397.48Z',
        duration: 3.5,
        mode: 'loop',
      },
      // c2: motionPath around the small arrow tip near the front
      {
        path: 'M348.81 397.59L354.3 403.42C355.8 405.22 356.42 407.15 356.16 408.98L380.09 396.79L348.82 397.59H348.81Z',
        duration: 2.0,
        mode: 'loop',
      },
    ],
  },
];

/**
 * Inset container background — three stacked layers (CSS background-image
 * paints first-listed on top):
 *   1. 90deg @ 20% black — flat darken tint
 *   2. 180deg #31263d50 → #392f4750 — translucent purple gradient
 *   3. /assets/patterns/diagonal.svg — diagonal-stripe pattern, tiled
 *
 * The pattern SVG is a 100×100 tile of #46414C strokes designed to repeat.
 * We keep the SVG (vs the PNG sibling) for crispness across breakpoints and
 * DPRs and so the stripe color is recolorable from a single fill= attribute.
 */
const INSET_BG_STYLE: React.CSSProperties = {
  backgroundImage: [
    'linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.2) 100%)',
    'linear-gradient(180deg, rgba(49, 38, 61, 0.5) 0%, rgba(57, 47, 71, 0.5) 100%)',
    "url('/assets/patterns/diagonal.svg')",
  ].join(', '),
  backgroundRepeat: 'no-repeat, no-repeat, repeat',
};

export default function CompetitorsSection() {
  const reduce = useReducedMotion();

  return (
    <section
      aria-labelledby="competitors-heading"
      className="relative isolate overflow-hidden px-6 pb-12 pt-6 sm:px-12 sm:pb-16 sm:pt-12"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20 bg-gradient-to-b from-[#463e57] to-[#31263d]"
      />
      <img
        src="/assets/images/backgrounds/players.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full select-none object-cover opacity-80"
      />

      <div className="relative mx-auto flex w-full max-w-[1180px] flex-col gap-8 sm:gap-12">
        <h2
          id="competitors-heading"
          className="font-[Orbitron] font-medium uppercase tracking-[-0.02em] text-white text-[clamp(20px,3.2vw,32px)]"
        >
          Our Competitors
        </h2>

        <div
          className="relative grid grid-cols-2 gap-3 w-full max-w-[1091px] mx-auto p-3 sm:gap-5 sm:p-8 md:grid-cols-3 md:gap-6 md:p-12"
          style={INSET_BG_STYLE}
        >
          {PLAYERS.map((player, i) => (
            <motion.figure
              key={player.slug}
              className="m-0 flex flex-col items-center gap-2 sm:gap-4"
              initial={reduce ? false : { opacity: 0, y: 24 }}
              whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={
                reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut', delay: i * 0.08 }
              }
            >
              <PlayerHex player={player} />
              <figcaption className="font-[Orbitron] font-medium uppercase text-white text-[clamp(11px,1.6vw,24px)]">
                {player.name}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
